/**
 * How a library model moves (ADR-043; spec "Engine behaviour"): the timing
 * the headset's engine plays and the validator counts, in one place, so a
 * step the validator says takes 9 s takes 9 s on the Quest. Pure, no
 * three.js and no imports. Numbers from the dino spike (`spike/dino-wow`):
 * the charge ramps up over 0.8 s and brakes at 5 m/s².
 *
 * Angles are degrees in the rig's yaw convention (models.ts: 0 faces −z,
 * positive turns left); distances metres; times seconds.
 */
export const MODEL_MOTION = {
    /** A walk speeds up to its gait's speed over this long. */
    ramp: 0.8,
    /** And brakes at this, m/s², to stop where it walks to. */
    brake: 5,
    /** Before a walk it turns on the spot at most this fast, degrees per second... */
    turnRate: 90,
    /** ...its gait's clip playing at this share of its speed meanwhile. */
    turnGait: 0.5,
    /** A face takes at least this long, and |angle| / `turnRate` beyond. */
    faceMin: 0.8,
    /**
     * A step's `turn` (a model shown from another side, "Wat wil je maken?" plan 4) turns in place at most
     * this fast, degrees per second, eased, and takes at least `showTurnMin` seconds: slow enough to read as
     * the model being turned round for the class to look at, a quarter turn in 2 s.
     */
    showTurnRate: 45,
    showTurnMin: 2,
    /** A turn smaller than this, degrees, is no turn at all. */
    noTurn: 0.5,
    /** A gait's clip never plays slower than this share of its speed. */
    timeScaleMin: 0.25,
    /** Cross-fades between clips: in general, into `bijten`, and back to `staan`. */
    fade: 0.35,
    fadeBite: 0.25,
    fadeIdle: 0.8,
};
/** The short way round from `from` to `to`, degrees in (−180, 180]. */
export function shortestTurn(from, to) {
    let delta = (to - from) % 360;
    if (delta > 180)
        delta -= 360;
    if (delta <= -180)
        delta += 360;
    return delta;
}
/** Seconds a turn on the spot takes before a walk. */
export function turnSeconds(from, to) {
    return Math.abs(shortestTurn(from, to)) / MODEL_MOTION.turnRate;
}
/** Where a turn on the spot is after `t` seconds: steady, the short way round. */
export function turnAt(t, from, to) {
    const total = turnSeconds(from, to);
    const k = total <= 0 ? 1 : Math.min(1, Math.max(0, t / total));
    return from + shortestTurn(from, to) * k;
}
/** Seconds `face: "pupil"` takes: max(0.8, |angle| / 90°). */
export function faceSeconds(from, to) {
    return Math.max(MODEL_MOTION.faceMin, Math.abs(shortestTurn(from, to)) / MODEL_MOTION.turnRate);
}
/** Where a face is after `t` seconds: eased in and out (smoothstep) over `faceSeconds`. */
export function faceAt(t, from, to) {
    const k = Math.min(1, Math.max(0, t / faceSeconds(from, to)));
    return from + shortestTurn(from, to) * k * k * (3 - 2 * k);
}
/**
 * Seconds a step's `turn` takes: max(2, |angle| / 45°), the short way round; 0 when it is already
 * turned so (within `noTurn`).
 */
export function showTurnSeconds(from, to) {
    const angle = Math.abs(shortestTurn(from, to));
    if (angle < MODEL_MOTION.noTurn)
        return 0;
    return Math.max(MODEL_MOTION.showTurnMin, angle / MODEL_MOTION.showTurnRate);
}
/** Where a step's `turn` is after `t` seconds: eased in and out (smoothstep) over `showTurnSeconds`. */
export function showTurnAt(t, from, to) {
    const total = showTurnSeconds(from, to);
    if (total <= 0)
        return from;
    const k = Math.min(1, Math.max(0, t / total));
    return from + shortestTurn(from, to) * k * k * (3 - 2 * k);
}
/**
 * A straight walk of `distance` at a gait of `speed`: up to speed over
 * 0.8 s, on at it, braking at 5 m/s² to stop there. Too short to reach the
 * speed, it brakes from wherever the ramp met the braking curve.
 */
function profile(distance, speed) {
    if (!(distance > 0) || !(speed > 0))
        return { peak: 0, t1: 0, t2: 0, total: 0 };
    const accel = speed / MODEL_MOTION.ramp;
    const brake = MODEL_MOTION.brake;
    const ramping = (speed * MODEL_MOTION.ramp) / 2;
    const braking = (speed * speed) / (2 * brake);
    if (distance >= ramping + braking) {
        const t2 = MODEL_MOTION.ramp + (distance - ramping - braking) / speed;
        return { peak: speed, t1: MODEL_MOTION.ramp, t2, total: t2 + speed / brake };
    }
    const peak = Math.sqrt((2 * distance * accel * brake) / (accel + brake));
    const t1 = peak / accel;
    return { peak, t1, t2: t1, total: t1 + peak / brake };
}
/** Seconds a straight walk takes, the ramp and the brake included. */
export function walkSeconds(distance, speed) {
    return profile(distance, speed).total;
}
/** How far along a straight walk is after `t` seconds, and how fast it goes then. */
export function walkAt(t, distance, speed) {
    const { peak, t1, t2, total } = profile(distance, speed);
    if (total <= 0 || t >= total)
        return { distance: Math.max(0, distance), speed: 0 };
    if (t <= 0)
        return { distance: 0, speed: 0 };
    const accel = peak / t1;
    if (t < t1)
        return { distance: (accel * t * t) / 2, speed: accel * t };
    const ramped = (peak * t1) / 2;
    if (t < t2)
        return { distance: ramped + peak * (t - t1), speed: peak };
    const braking = t - t2;
    return {
        distance: Math.min(distance, ramped + peak * (t2 - t1) + peak * braking - (MODEL_MOTION.brake * braking * braking) / 2),
        speed: Math.max(0, peak - MODEL_MOTION.brake * braking),
    };
}
/**
 * The same walk frame by frame, for an engine that follows a moving target
 * (Spannend's walk to the pupil): the speed after `elapsed` seconds with
 * `left` metres to go -- the ramp, the gait's speed or the braking curve,
 * whichever is lowest.
 */
export function walkSpeed(elapsed, left, speed) {
    const ramp = speed * Math.min(1, Math.max(0, elapsed) / MODEL_MOTION.ramp);
    const brake = Math.sqrt(2 * MODEL_MOTION.brake * Math.max(left, 0));
    return Math.max(0, Math.min(ramp, speed, brake));
}
/** How fast a gait's clip plays at ground speed `v`, so its feet never slide: max(0.25, v / speed). */
export function clipTimeScale(v, speed) {
    if (!(speed > 0))
        return 1;
    return Math.max(MODEL_MOTION.timeScaleMin, v / speed);
}
//# sourceMappingURL=model-motion.js.map